import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Tabs, Tab, Grid, TextField, Button, MenuItem,
  Alert, Snackbar, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import { Add, Delete, CurrencyExchange, CalendarMonth, Public, Sync } from '@mui/icons-material';
import {
  getCurrencyProfile, updateCurrencyProfile, getExchangeRates, createExchangeRate, deleteExchangeRate,
  convertCurrency, getCalendars, saveCalendar, deleteCalendar, syncExchangeRates,
  type WorkingCalendar,
} from '../../services/platform/localeService';

export default function LocalePage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState('');
  const [convertForm, setConvertForm] = useState({ amount: '100', from: 'USD', to: 'INR' });
  const [convertResult, setConvertResult] = useState('');
  const [rateForm, setRateForm] = useState({ fromCurrency: 'USD', toCurrency: 'INR', rate: '' });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarForm, setCalendarForm] = useState<WorkingCalendar>({
    name: '', scope: 'global', timezone: 'Asia/Kolkata', workingDays: [1, 2, 3, 4, 5],
    weekendDays: [0, 6], holidays: [], isActive: true,
  });

  const { data: profile } = useQuery({ queryKey: ['currency-profile'], queryFn: getCurrencyProfile });
  const { data: rates = [] } = useQuery({ queryKey: ['exchange-rates'], queryFn: getExchangeRates });
  const { data: calendars = [] } = useQuery({ queryKey: ['calendars'], queryFn: getCalendars });

  const [syncInfo, setSyncInfo] = useState('');

  const saveProfile = useMutation({
    mutationFn: updateCurrencyProfile,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['currency-profile'] }); setSnack('Currency profile saved'); },
  });

  const syncRates = useMutation({
    mutationFn: () => syncExchangeRates(profile?.baseCurrency),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
      setSyncInfo(`Last sync: ${r.date || 'now'} via ${r.provider} (${r.synced} rates, base ${r.base})`);
      setSnack(`Synced ${r.synced} exchange rates`);
    },
    onError: () => setSnack('FX sync failed — check currency masters and network'),
  });

  const addRate = useMutation({
    mutationFn: createExchangeRate,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['exchange-rates'] }); setRateForm({ fromCurrency: 'USD', toCurrency: 'INR', rate: '' }); setSnack('Exchange rate added'); },
  });

  const removeRate = useMutation({
    mutationFn: deleteExchangeRate,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['exchange-rates'] }); setSnack('Rate removed'); },
  });

  const saveCal = useMutation({
    mutationFn: saveCalendar,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['calendars'] }); setCalendarOpen(false); setSnack('Calendar saved'); },
  });

  const removeCal = useMutation({
    mutationFn: deleteCalendar,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['calendars'] }); setSnack('Calendar removed'); },
  });

  const rateColumns: GridColDef[] = [
    { field: 'fromCurrency', headerName: 'From', width: 90 },
    { field: 'toCurrency', headerName: 'To', width: 90 },
    { field: 'rate', headerName: 'Rate', width: 120 },
    { field: 'source', headerName: 'Source', width: 100, renderCell: (p) => (
      <Chip size="small" label={p.value} color={p.value === 'manual' ? 'warning' : 'success'} variant="outlined" />
    ) },
    { field: 'effectiveFrom', headerName: 'Effective From', width: 180, valueFormatter: (v) => (v ? new Date(String(v)).toLocaleString() : '') },
    {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={() => removeRate.mutate(p.row._id)}>
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const calColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'scope', headerName: 'Scope', width: 100 },
    { field: 'country', headerName: 'Country', width: 90 },
    { field: 'timezone', headerName: 'Timezone', width: 140 },
    { field: 'holidays', headerName: 'Holidays', width: 90, valueGetter: (_v, row) => row.holidays?.length ?? 0 },
    {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={() => removeCal.mutate(p.row._id)}>
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Locale & Multi-Currency</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure currencies, exchange rates, and working calendars. Country details are managed in Master Data → Countries.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<CurrencyExchange />} iconPosition="start" label="Multi-Currency" />
          <Tab icon={<Public />} iconPosition="start" label="Exchange Rates" />
          <Tab icon={<CalendarMonth />} iconPosition="start" label="Working Calendars" />
        </Tabs>
      </Paper>

      {tab === 0 && profile && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {(['baseCurrency', 'displayCurrency', 'settlementCurrency', 'reportingCurrency'] as const).map((key) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <TextField
                  fullWidth size="small" label={key.replace(/([A-Z])/g, ' $1')}
                  defaultValue={profile[key]}
                  onBlur={(e) => saveProfile.mutate({ [key]: e.target.value.toUpperCase() })}
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {profile.countryOverrides?.map((o) => (
              <Chip key={o.country} label={`${o.country}: ${o.displayCurrency || profile.displayCurrency}`} size="small" />
            ))}
          </Box>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>FX Converter</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Amount" value={convertForm.amount} onChange={(e) => setConvertForm({ ...convertForm, amount: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="From" value={convertForm.from} onChange={(e) => setConvertForm({ ...convertForm, from: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="To" value={convertForm.to} onChange={(e) => setConvertForm({ ...convertForm, to: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="contained" onClick={async () => {
                  const r = await convertCurrency(Number(convertForm.amount), convertForm.from, convertForm.to);
                  setConvertResult(`${r.formatted} (rate: ${r.rate})`);
                }}>Convert</Button>
              </Grid>
            </Grid>
            {convertResult && <Alert severity="info" sx={{ mt: 1 }}>{convertResult}</Alert>}
          </Box>
        </Paper>
      )}

      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Sync />}
                onClick={() => syncRates.mutate()}
                disabled={syncRates.isPending}
              >
                Sync from API (Frankfurter)
              </Button>
              <Typography variant="caption" color="text.secondary">
                Free live rates — manual rates always override API rates
              </Typography>
            </Box>
            {syncInfo && <Alert severity="success" sx={{ mb: 2 }}>{syncInfo}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" label="From" value={rateForm.fromCurrency} onChange={(e) => setRateForm({ ...rateForm, fromCurrency: e.target.value })} />
            <TextField size="small" label="To" value={rateForm.toCurrency} onChange={(e) => setRateForm({ ...rateForm, toCurrency: e.target.value })} />
            <TextField size="small" label="Rate" type="number" value={rateForm.rate} onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })} />
            <Button variant="contained" startIcon={<Add />} disabled={!rateForm.rate} onClick={() => addRate.mutate({ ...rateForm, rate: Number(rateForm.rate), source: 'manual' })}>Add Manual Rate</Button>
            </Box>
          </Paper>
          <Paper sx={{ height: { xs: 360, sm: 420 }, overflow: 'hidden' }}>
            <SerialNoDataGrid rows={rates} columns={rateColumns} getRowId={(r) => r._id} disableRowSelectionOnClick />
          </Paper>
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setCalendarForm({ name: '', scope: 'global', timezone: 'Asia/Kolkata', workingDays: [1, 2, 3, 4, 5], weekendDays: [0, 6], holidays: [], isActive: true }); setCalendarOpen(true); }}>Add Calendar</Button>
          </Box>
          <Paper sx={{ height: { xs: 360, sm: 420 }, overflow: 'hidden' }}>
            <SerialNoDataGrid rows={calendars} columns={calColumns} getRowId={(r) => r._id!} disableRowSelectionOnClick />
          </Paper>
        </Box>
      )}

      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Working Calendar</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth label="Name" value={calendarForm.name} onChange={(e) => setCalendarForm({ ...calendarForm, name: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Scope" value={calendarForm.scope} onChange={(e) => setCalendarForm({ ...calendarForm, scope: e.target.value })}>
                {['global', 'country', 'state', 'city', 'branch'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth label="Timezone" value={calendarForm.timezone} onChange={(e) => setCalendarForm({ ...calendarForm, timezone: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Country" value={calendarForm.country || ''} onChange={(e) => setCalendarForm({ ...calendarForm, country: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Working Hours" placeholder="09:00 - 18:00" value={`${calendarForm.workingHoursStart || ''} - ${calendarForm.workingHoursEnd || ''}`} onChange={(e) => {
              const [start, end] = e.target.value.split('-').map((s) => s.trim());
              setCalendarForm({ ...calendarForm, workingHoursStart: start, workingHoursEnd: end });
            }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveCal.mutate(calendarForm)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
